import { Button, FormLayout, Heading, LoadingDots, Paragraph, Text } from '@expressable/syntax-ds';
import { CheckFat, Warning } from '@phosphor-icons/react';
import { useSignupRoutingRules } from 'domains/signupDirect/businessLogic/hooks';
import { useSignupDirectCtx } from 'domains/signupDirect/context';
import { DIRECT_SIGNUP_MIXPANEL_EVENTS } from 'domains/signupDirect/misc/mixpanelTracking';
import { useSignupNavigationGuard } from 'domains/signupDirect/misc/useSignupNavigationGuard';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useBoolean } from 'react-use';
import { routes } from 'router/routes';
import { useTrack } from 'utils/hooks/useMixPanel';
import { ChoiceCard } from './ChoiceCard';

/**
 * @TODO(juan)
 *
 * a bunch of code is commented here while landing page is experimented with
 */

export const SignupScheduleLandingPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [pickedRoute, setPickedRoute] = useState<'single' | 'split'>('split');

  const { isLoading } = useForcedRedirects();
  useSignupNavigationGuard();

  const { track } = useTrack();

  if (isLoading) {
    return <LoadingDots className="flex flex-col h-max static" />;
  }
  return (
    <>
      <div>
        <Heading level="h1">{t('directSignup.landingPage.title')}</Heading>
        <Paragraph size="lg">{t('directSignup.landingPage.subTitle')}</Paragraph>
      </div>

      <div className="grid grid-cols-2 gap-e24 mobile-d:grid-cols-1">
        <ChoiceCard
          title={t('directSignup.landingPage.options.splits.title')}
          onClick={() => setPickedRoute('split')}
          checked={pickedRoute === 'split'}
          className="relative"
        >
          <div className="absolute -top-e16 left-e32 rounded-xs bg-purple-500 text-white py-e8 px-e12 flex items-center">
            <Text variant="overline">{t('directSignup.landingPage.mostPopularPopup')}</Text>
          </div>
          <Heading level="h5" noDefaultMargins className="mb-e24">
            {t('directSignup.landingPage.options.splits.description')}
          </Heading>
          <ul className="flex flex-col gap-e24">
            {OPTIONS_ITEMS.SPLITS.map(({ icon: Icon, title }) => (
              <li className="flex gap-e16">
                <Icon weight="fill" className="size-e24 shrink-0    " />
                <Text variant="body-md">{t(title)}</Text>
              </li>
            ))}
          </ul>
        </ChoiceCard>
        <ChoiceCard
          title={t('directSignup.landingPage.options.single.title')}
          onClick={() => setPickedRoute('single')}
          checked={pickedRoute === 'single'}
        >
          <Heading level="h5" noDefaultMargins className="mb-e24">
            {t('directSignup.landingPage.options.single.description')}
          </Heading>
          <ul className="flex flex-col gap-e24">
            {OPTIONS_ITEMS.SINGLE.map(({ icon: Icon, title }) => (
              <li className="flex gap-e16">
                <Icon weight="fill" className="size-e24 shrink-0" />
                <Text variant="body-md">{t(title)}</Text>
              </li>
            ))}
          </ul>
        </ChoiceCard>
      </div>
      <FormLayout className="mb-e128">
        <FormLayout.CTA sticky>
          <Button
            onClick={() => {
              if (pickedRoute === 'single') {
                track(DIRECT_SIGNUP_MIXPANEL_EVENTS.STEPS.LANDING_PAGE.CHOSEN_SINGLE);
                navigate(routes.signupDirect.evaluation.schedule.single);
              } else if (pickedRoute === 'split') {
                track(DIRECT_SIGNUP_MIXPANEL_EVENTS.STEPS.LANDING_PAGE.CHOSEN_SPLIT);
                navigate(routes.signupDirect.evaluation.schedule.split.first);
              }
            }}
          >
            {t('directSignup.landingPage.cta')}
          </Button>
        </FormLayout.CTA>
      </FormLayout>
    </>
  );
};

// Handles forced redirect in case a choice isn't available to the user
const useForcedRedirects = () => {
  const [isLoading] = useBoolean(true);
  const { filters, isResumedLead, onContinueToJotformRegistrationForm, evaluationSchedule } = useSignupDirectCtx();
  if (!filters) throw new Error('Expected filters to be set');

  const navigate = useNavigate();

  const { track } = useTrack();

  const { isEvalAllowed, isSplitEvaluationAllowed, isScheduleAllowed } = useSignupRoutingRules();

  // const { evaluationCalendarQuery: splitsQuery } = useSignupEvaluationCalendar(filters, EvaluationDuration.Split);
  // const { isAvailabilityOverMinimum: areSplitsAvailable } = useMinimumAvailability({
  //   disabled: true,
  //   evaluationCalendarQuery: splitsQuery,
  // });

  // const { evaluationCalendarQuery: singlesQuery } = useSignupEvaluationCalendar(filters, EvaluationDuration.Single);
  // const { isAvailabilityOverMinimum: areSinglesAvailable } = useMinimumAvailability({
  //   disabled: true,
  //   evaluationCalendarQuery: singlesQuery,
  // });

  useEffect(() => {
    // Prefilled appointment - skip schedule picking
    if (!isScheduleAllowed.allowed) {
      track(DIRECT_SIGNUP_MIXPANEL_EVENTS.STEPS.EVAL.SCHEDULE.FORCED_REDIRECTS.PREFILLED_APPOINTMENT, {
        isResumedLead,
        reason: isScheduleAllowed.reason,
        step: DIRECT_SIGNUP_MIXPANEL_EVENTS.STEPS.LANDING_PAGE,
      });

      if (!evaluationSchedule) {
        throw new Error('Expected evaluationSchedule to be set when using prefilled appointment');
      }
      navigate(routes.signupDirect.evaluation.details, { replace: true });
      return;
    }
    // Evaluation allowed or not
    if (!isEvalAllowed.allowed) {
      track(DIRECT_SIGNUP_MIXPANEL_EVENTS.STEPS.EVAL.EXCLUSION, {
        isResumedLead,
        reason: isEvalAllowed.reason,
        step: DIRECT_SIGNUP_MIXPANEL_EVENTS.STEPS.LANDING_PAGE,
      });
      onContinueToJotformRegistrationForm();
      return;
    }

    // Split evaluation allowed or not
    if (!isSplitEvaluationAllowed.allowed) {
      track(DIRECT_SIGNUP_MIXPANEL_EVENTS.STEPS.EVAL.SCHEDULE.FORCED_REDIRECTS.SPLITS_NOT_ALLOWED, {
        isResumedLead,
        reason: isSplitEvaluationAllowed.reason,
        step: DIRECT_SIGNUP_MIXPANEL_EVENTS.STEPS.LANDING_PAGE,
      });
      navigate(routes.signupDirect.evaluation.schedule.single, { replace: true });
      return;
    }

    if (isSplitEvaluationAllowed.allowed) {
      navigate(routes.signupDirect.evaluation.schedule.split.first, { replace: true });
      return;
    }

    // Availability checks

    // if (areSplitsAvailable) {
    //   if (!areSinglesAvailable) {
    //     track(DIRECT_SIGNUP_MIXPANEL_EVENTS.STEPS.EVAL.SCHEDULE.FORCED_REDIRECTS.SINGLES_UNAVAILABLE, {
    //       isResumedLead,
    //     });
    //     navigate(routes.signupDirect.evaluation.schedule.split.first, { replace: true });
    //     return;
    //   } else {
    //     // Ok, both available
    //   }
    // }

    // if (!areSplitsAvailable) {
    //   if (areSinglesAvailable) {
    //     track(DIRECT_SIGNUP_MIXPANEL_EVENTS.STEPS.EVAL.SCHEDULE.FORCED_REDIRECTS.SPLITS_UNAVAILABLE, {
    //       isResumedLead,
    //     });
    //     navigate(routes.signupDirect.evaluation.schedule.single, { replace: true });
    //     return;
    //   }
    //   if (!areSinglesAvailable) {
    //     track(DIRECT_SIGNUP_MIXPANEL_EVENTS.STEPS.EVAL.SCHEDULE.FORCED_REDIRECTS.ALL_UNAVAILABLE, {
    //       isResumedLead,
    //     });
    //     onContinueToConsult(true);
    //     return;
    //   }
    // }

    // track(DIRECT_SIGNUP_MIXPANEL_EVENTS.STEPS.LANDING_PAGE.BASE);
    // @TODO experiment: landing page disabled for now
    // setIsLoading(false);
  }, [isEvalAllowed, isSplitEvaluationAllowed]);

  return { isLoading };
};

const OPTIONS_ITEMS = {
  SPLITS: [
    {
      title: 'directSignup.landingPage.options.splits.items.1',
      icon: CheckFat,
    },
    {
      title: 'directSignup.landingPage.options.splits.items.2',
      icon: CheckFat,
    },
    {
      title: 'directSignup.landingPage.options.splits.items.3',
      icon: CheckFat,
    },
  ],
  SINGLE: [
    {
      title: 'directSignup.landingPage.options.single.items.1',
      icon: CheckFat,
    },
    {
      title: 'directSignup.landingPage.options.single.items.2',
      icon: CheckFat,
    },
    {
      title: 'directSignup.landingPage.options.single.items.3',
      icon: Warning,
    },
  ],
};
